export const COMMENT_DISPLAY_WIDTH_PX = 360;
export const COMMENT_DISPLAY_COMPACT_WIDTH_PX = 48;
export const COMMENT_DISPLAY_VIEWPORT_PADDING_PX = 12;
export const COMMENT_DISPLAY_SCROLL_END_THRESHOLD_PX = 2;
export const COMMENT_DISPLAY_FLOATING_PADDING_PX = 16;
export const COMMENT_DISPLAY_FLOATING_OFFSET_PX = 10;
export const COMMENT_DISPLAY_MIN_AVAILABLE_HEIGHT_PX = 120;
export const COMMENT_DISPLAY_INLINE_REPLY_RESERVED_HEIGHT_PX = 72;
export const COMMENT_DISPLAY_BOUNDARY_ATTR = 'data-comment-display-boundary';

type CommentDisplayAnchorRect = Pick<DOMRect, 'top' | 'bottom'>;

/**
 * Returns the available comment display height from the stable anchor rect, not from the
 * floating element itself. Using the floating element's top creates a feedback loop because
 * Floating UI moves that top when the comment grows.
 */
export const getCommentDisplayMaxHeightForAnchor = (
  anchorRect: CommentDisplayAnchorRect | null | undefined,
  placement: string | null | undefined,
  viewportHeight: number = window.innerHeight,
  viewportPadding: number = COMMENT_DISPLAY_FLOATING_PADDING_PX,
  floatingOffset: number = COMMENT_DISPLAY_FLOATING_OFFSET_PX
) => {
  if (!anchorRect) {
    return undefined;
  }

  const placementSide = placement?.split('-')[0];
  const spaceAbove = anchorRect.top - viewportPadding - floatingOffset;
  const spaceBelow = viewportHeight - anchorRect.bottom - viewportPadding - floatingOffset;
  const availableSpace =
    placementSide === 'top'
      ? spaceAbove
      : placementSide === 'bottom'
        ? spaceBelow
        : Math.max(spaceAbove, spaceBelow);

  return availableSpace > 0
    ? Math.max(COMMENT_DISPLAY_MIN_AVAILABLE_HEIGHT_PX, Math.floor(availableSpace))
    : undefined;
};

/**
 * Clamps a comment box's viewport-relative `top` so its bottom edge stays visible.
 *
 * `desiredTop` is the comment's natural top position in viewport coordinates.
 * `commentHeight` is the rendered height of the expanded comment box.
 * `viewportHeight` is the current viewport height, typically `window.innerHeight`.
 * `viewportPadding` reserves a small gap between the comment and the viewport bottom.
 *
 * The logic is intentionally one-sided: it preserves the natural anchored position unless the
 * comment would extend past the viewport bottom, in which case it shifts the comment upward just
 * enough to keep the bottom edge visible.
 */
export const clampCommentDisplayTop = (
  desiredTop: number,
  commentHeight: number,
  viewportHeight: number,
  viewportPadding: number = COMMENT_DISPLAY_VIEWPORT_PADDING_PX
) => {
  if (commentHeight <= 0) {
    return desiredTop;
  }

  return Math.min(desiredTop, viewportHeight - commentHeight - viewportPadding);
};

/**
 * Returns whether the page is effectively scrolled to the bottom.
 *
 * `scrollTop` is the current top scroll offset of the document.
 * `viewportHeight` is the visible viewport height.
 * `scrollHeight` is the total scrollable document height.
 * `threshold` is a small tolerance so near-bottom values still count as "at the end".
 *
 * The helper is used to gate the upward clamp so we only intervene in the rare bottom-of-page
 * case where the comment would otherwise be cut off and the user has no remaining downward scroll
 * room to reveal it naturally.
 */
export const shouldClampCommentDisplayToViewportBottom = (
  scrollTop: number,
  viewportHeight: number,
  scrollHeight: number,
  threshold: number = COMMENT_DISPLAY_SCROLL_END_THRESHOLD_PX
) => scrollTop + viewportHeight >= scrollHeight - threshold;

export const getCommentDisplayTopBoundary = (
  stickyOffsetTop: number,
  viewportPadding: number = COMMENT_DISPLAY_VIEWPORT_PADDING_PX
) => stickyOffsetTop + viewportPadding;

export const clampCommentDisplayTopToBoundary = (desiredTop: number, topBoundary: number) => {
  if (!Number.isFinite(desiredTop) || !Number.isFinite(topBoundary)) {
    return desiredTop;
  }

  return Math.max(desiredTop, topBoundary);
};

export const getCommentDisplayMaxHeightForTop = (
  top: number,
  viewportHeight: number = window.innerHeight,
  viewportPadding: number = COMMENT_DISPLAY_VIEWPORT_PADDING_PX
) => {
  if (!Number.isFinite(top)) {
    return undefined;
  }

  const availableSpace = viewportHeight - top - viewportPadding;
  return availableSpace > 0
    ? Math.max(COMMENT_DISPLAY_MIN_AVAILABLE_HEIGHT_PX, Math.floor(availableSpace))
    : undefined;
};

type InlineCommentDisplayVerticalPosition = {
  placement: 'top' | 'bottom';
  top: number;
  maxHeight?: number;
};

export const getInlineCommentDisplayVerticalPosition = (
  anchorRect: CommentDisplayAnchorRect,
  topBoundary: number,
  viewportHeight: number = window.innerHeight,
  viewportPadding: number = COMMENT_DISPLAY_VIEWPORT_PADDING_PX,
  floatingOffset: number = COMMENT_DISPLAY_FLOATING_OFFSET_PX
): InlineCommentDisplayVerticalPosition => {
  const desiredBottomTop = anchorRect.bottom + floatingOffset;
  const bottomTop = clampCommentDisplayTopToBoundary(desiredBottomTop, topBoundary);
  const spaceBelow = viewportHeight - desiredBottomTop - viewportPadding;
  const spaceAbove = anchorRect.top - topBoundary - floatingOffset;

  if (
    spaceBelow >= COMMENT_DISPLAY_MIN_AVAILABLE_HEIGHT_PX ||
    spaceAbove < COMMENT_DISPLAY_MIN_AVAILABLE_HEIGHT_PX
  ) {
    return {
      placement: 'bottom',
      top: bottomTop,
      maxHeight: getCommentDisplayMaxHeightForTop(bottomTop, viewportHeight, viewportPadding),
    };
  }

  const maxHeight = Math.floor(spaceAbove);

  return {
    placement: 'top',
    top: anchorRect.top - floatingOffset,
    maxHeight,
  };
};

export const getInlineCommentBoxMaxHeight = (
  maxHeightForInlineComment: number | undefined,
  replyReservedHeight: number = COMMENT_DISPLAY_INLINE_REPLY_RESERVED_HEIGHT_PX
) => {
  if (maxHeightForInlineComment === undefined) {
    return undefined;
  }

  return Math.max(0, Math.floor(maxHeightForInlineComment - replyReservedHeight));
};

type CommentDisplayHorizontalBounds = {
  left: number;
  right: number;
};

type CommentDisplayHorizontalBoundsOptions = {
  rootDocument?: Document;
  viewportPadding?: number;
  viewportWidth?: number;
};

export const getCommentDisplayHorizontalBounds = (
  editorRoot: HTMLElement | null | undefined,
  options: CommentDisplayHorizontalBoundsOptions = {}
): CommentDisplayHorizontalBounds => {
  const rootDocument =
    options.rootDocument ??
    editorRoot?.ownerDocument ??
    (typeof document !== 'undefined' ? document : undefined);
  const viewportWidth =
    options.viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 0);
  const viewportPadding = options.viewportPadding ?? COMMENT_DISPLAY_FLOATING_PADDING_PX;
  const viewportBounds = {
    left: viewportPadding,
    right: viewportWidth - viewportPadding,
  };

  const boundaryElement =
    editorRoot?.closest<HTMLElement>(`[${COMMENT_DISPLAY_BOUNDARY_ATTR}]`) ??
    rootDocument?.getElementById('main') ??
    null;
  const boundaryRect = boundaryElement?.getBoundingClientRect();

  if (!boundaryRect || boundaryRect.width <= 0 || boundaryRect.right <= boundaryRect.left) {
    return viewportBounds;
  }

  const left = Math.max(viewportBounds.left, boundaryRect.left + viewportPadding);
  const right = Math.max(
    left,
    Math.min(viewportBounds.right, boundaryRect.right - viewportPadding)
  );

  return {left, right};
};

export const clampCommentDisplayLeft = (
  desiredLeft: number,
  commentWidth: number,
  bounds: CommentDisplayHorizontalBounds
) => {
  if (!Number.isFinite(desiredLeft)) {
    return desiredLeft;
  }

  const resolvedWidth = Number.isFinite(commentWidth) && commentWidth > 0 ? commentWidth : 0;
  const maxLeft = Math.max(bounds.left, bounds.right - resolvedWidth);

  return Math.min(Math.max(desiredLeft, bounds.left), maxLeft);
};
